import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Container,
  VStack,
  HStack,
  Text,
  Badge,
  Button,
  Flex,
  useColorModeValue,
  Spinner,
  Alert,
  AlertIcon,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverArrow,
  PopoverCloseButton,
  PopoverBody,
  Checkbox,
  Tag,
  TagLabel,
  TagCloseButton,
  SimpleGrid,
  Select,
} from '@chakra-ui/react';
import { useSearchParams } from 'react-router-dom';
import { useDeals, Deal } from '../hooks/useDeals';
import { useMyLeagueStore } from '../store/myLeagueStore';
import TradeModal from './TradeModal';
import PageTitle from './PageTitle';
import TeamLogo from './TeamLogo';
import { getTeamInfoById } from '../utils/getTeamInfo';
import { useUserContext } from '../contexts/UserContext';
import { getStatusColor, getStatusText } from '../utils/tradeStatusUtils';
import { getExpiryCountdown } from '../lib/pstTime';
import { FaReply } from 'react-icons/fa';
import GlassCard from './GlassCard';

interface TradesProps {
  isMyTrades?: boolean; // true for /my-trades, false for /trades
}

const tabOptions = [
  { value: 'pending', label: 'Pending' },
  { value: 'completed', label: 'Completed' },
  { value: 'terminated', label: 'Terminated' },
  { value: 'all', label: 'All' },
];

const Trades: React.FC<TradesProps> = ({ isMyTrades = false }) => {
  
  const [selectedDeal, setSelectedDeal] = useState<Deal | null>(null);
  const [isTradeModalOpen, setIsTradeModalOpen] = useState(false);
  const [isNewTradeModalOpen, setIsNewTradeModalOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('pending');
  const [selectedStatuses, setSelectedStatuses] = useState<Deal['status'][]>([]);
  const [statusPopoverOpen, setStatusPopoverOpen] = useState(false);
  const [pendingStatuses, setPendingStatuses] = useState<Deal['status'][]>([]);
  const hasInitialized = useRef(false);
  const isFetching = useRef(false);
  const canAutoOpenModal = useRef(true);
  const { myTeam } = useUserContext();
  
  // Get search params for deal parameter
  const [searchParams] = useSearchParams();

  
  const textColor = useColorModeValue('gray.800', 'white');
  const secondaryText = useColorModeValue('gray.600', 'gray.400');
  
  const myLeague = useMyLeagueStore(state => state.myLeague);
  
  const { 
    deals, 
    loading, 
    error, 
    getLeagueDeals
  } = useDeals(myLeague?.id);

  // Get the current user's team ID from their league affiliations
  const currentUserTeamId = myTeam;

  // Clear deals when league changes
  useEffect(() => {
    if (myLeague?.id) {
      // Clear existing deals when league changes
      setSelectedDeal(null);
      setIsTradeModalOpen(false);
      hasInitialized.current = false; // Reset initialization flag for new league
      isFetching.current = false; // Reset fetching flag for new league
      canAutoOpenModal.current = true; // Reset auto-open flag for new league
    }
  }, [myLeague?.id]);

  // Check for deal parameter in URL and automatically open TradeModal
  useEffect(() => {
    const dealId = searchParams.get('deal');
    if (dealId && myLeague?.id && deals.length > 0 && canAutoOpenModal.current) {
      // Find the deal with the specified ID
      const deal = deals.find(d => d.id.toString() === dealId);
      if (deal) {
        console.log('Trades: Auto-opening TradeModal for deal', dealId);
        setSelectedDeal(deal);
        setIsTradeModalOpen(true);
        canAutoOpenModal.current = false; // Prevent re-triggering
      }
    }
  }, [searchParams, myLeague?.id, deals]);

  // Reset fetching flag when loading completes
  useEffect(() => {
    if (!loading && isFetching.current) {
      console.log('Trades: Loading completed, resetting isFetching to false');
      isFetching.current = false;
    }
  }, [loading]);

  // Single effect to handle data loading - only runs when necessary

  const refreshDeals = () => {
    
    console.log('Trades: Setting isFetching to true');
    
    if (isMyTrades && currentUserTeamId) {
      // For my-trades, get deals for the current user's tea
      getLeagueDeals({ 
        teamId: currentUserTeamId, 
        statusList: selectedStatuses || [],
        includePlayers: true,
        type: 'trade' as Deal['type']
      });
    } else {
      // For trades, get all league deals with current status filters
      getLeagueDeals({ 
        statusList: selectedStatuses || [],  // Ensure it's always an array
        includePlayers: true,
        type: 'trade' as Deal['type']
      });
    }
    hasInitialized.current = true;
    console.log('Trades: API call made, hasInitialized set to true');
  };

  useEffect(() => {
    // Only proceed if we have a league ID, we're not already loading, and we're not already fetching
    if (myLeague?.id && !loading && !isFetching.current) {
      // For the first load, always fetch data
      // For subsequent loads, only fetch if statuses changed or it's a different league/team
      // const shouldFetch = !hasInitialized.current || 
      //                    (selectedStatuses.length > 0 && hasInitialized.current);
      const shouldFetch = true;
       
      if (shouldFetch) {
        isFetching.current = true;
        refreshDeals();
      }
    }
  }, [myLeague?.id, currentUserTeamId, isMyTrades, selectedStatuses]);

  const handleViewDeal = (deal: Deal) => {
    setSelectedDeal(deal);
    setIsTradeModalOpen(true);
  };

  const handleCloseTradeModal = () => {
    setIsTradeModalOpen(false);
    setSelectedDeal(null);
    
    // Reset the auto-open flag so it can work again if needed
    canAutoOpenModal.current = false;
    refreshDeals();

    // Note: We don't automatically refresh deals here to preserve filters
    // The deals will be refreshed when the user makes changes or when the component re-initializes
  };

  const handleCloseNewTradeModal = () => {
    setIsNewTradeModalOpen(false);
    refreshDeals();
  };

  // Map UI status values to API status values
  const mapStatusToApi = (status: string): Deal['status'] => {
    switch (status) {
      case 'Accepted':
        return 'accepted';
      case 'Proposed':
        return 'proposed';
      case 'Countered':
        return 'countered';
      case 'Approved':
        return 'approved';
      case 'Declined':
        return 'declined';
      case 'Expired':
        return 'expired';
      case 'Rejected':
        return 'rejected';
      case 'Withdrawn':
        return 'withdrawn';
      case 'Under review':
        return 'under_review';
      case 'League review':
        return 'league_review';
      case 'Has Salary Issue':
        return 'has_salary_issue';
      case 'Completed':
        return 'completed';
      default:
        return status as Deal['status'];
    }
  };



  // Toggle status selection (now works with pending statuses)
  const toggleStatus = (status: string) => {
    const apiStatus = mapStatusToApi(status);
    console.log('Trades: Toggling pending status', { uiStatus: status, apiStatus, currentPending: pendingStatuses });
    setPendingStatuses(prev => {
      const newStatuses = prev.includes(apiStatus) ? prev.filter(s => s !== apiStatus) : [...prev, apiStatus];
      console.log('Trades: New pending statuses', newStatuses);
      return newStatuses;
    });
  };

  // Remove a status filter from a chip
  const removeStatus = (status: Deal['status']) => {
    const s = selectedStatuses.filter(s => s !== status);
    setSelectedStatuses(s);
  };

  // Apply pending filters
  const applyFilters = () => {
    setSelectedStatuses([...pendingStatuses]);
    setStatusPopoverOpen(false);
    setPendingStatuses([]);
  };

  const renderDealCard = (deal: Deal) => (
    <GlassCard key={deal.id} h="full" display="flex" flexDirection="column" px={4}>
      <Box pb={1} pt={2}>
        <HStack justify="space-between" w="full" align="start">
          {/* Trade Title */}
          <Text fontSize="md" fontWeight="bold" color={textColor}>
            Trade
          </Text>
          
          {/* Status and Date */}
          <VStack align="end" spacing={1}>
            <Badge colorScheme={getStatusColor(deal.status)} size="sm">
              {(() => {
                // Check if trade should be marked as expired
                const statusLower = (deal.status || '').toLowerCase();
                // Don't mark as expired if status is already a terminal status
                const isTerminalStatus = ['rejected', 'canceled', 'completed', 'declined', 'withdrawn', 'approved'].includes(statusLower);
                const isExpired = !isTerminalStatus &&
                                 statusLower !== 'under_review' && 
                                 statusLower !== 'league_review' && 
                                 deal.expires && 
                                 new Date(deal.expires) < new Date();
                
                if (isExpired) {
                  return 'EXPIRED';
                }
                
                if (deal.status === 'proposed' || deal.status === 'countered') {
                  return (
                    <>
                      {(() => {
                        // Determine which team accepted the trade
                        if (deal.team1_accepted && myTeam === deal.team1) {
                          return 'You';
                        } else if (deal.team2_accepted && myTeam === deal.team2) {
                          return 'You';
                        } else if (deal.team1_accepted) {
                          return getTeamInfoById(deal.team1, myLeague)?.name || `Team ${deal.team1}`;
                        } else if (deal.team2_accepted) {
                          return getTeamInfoById(deal.team2, myLeague)?.name || `Team ${deal.team2}`;
                        }
                        return '';
                      })()} {getStatusText(deal.status)}
                    </>
                  );
                }
                
                return getStatusText(deal.status);
              })()}
            </Badge>
            <Text fontSize="xs" color={secondaryText}>
              {new Date(deal.updated).toLocaleDateString()}
            </Text>
          </VStack>
                </HStack>
      </Box>

      <Box pt={0} pb={2} display="flex" flexDirection="column" flex={1}>
        <VStack align="start" spacing={1} flex={1}>
          {/* Teams Section */}
          <HStack spacing={4} w="full" align="start" flex={1}>
            {/* Team 1 */}
            <VStack align="start" spacing={0} flex={1}>
              <Box w="full">
                <HStack spacing={0} align="center" justify="start">
                  <Box flexShrink={0}>
                    <TeamLogo 
                      team={{
                        name: getTeamInfoById(deal.team1, myLeague)?.name || `Team ${deal.team1}`,
                        logo: getTeamInfoById(deal.team1, myLeague)?.logo,
                        color_primary: getTeamInfoById(deal.team1, myLeague)?.color_primary,
                        color_secondary: getTeamInfoById(deal.team1, myLeague)?.color_secondary,
                        color_text: getTeamInfoById(deal.team1, myLeague)?.color_text
                      }} 
                      size="sm" 
                    />
                  </Box>
                  <Text ml={4} fontSize="s" fontWeight="semibold" color={secondaryText} flex={1}>  
                    {getTeamInfoById(deal.team1, myLeague)?.name || `Team ${deal.team1}`}
                    {deal.players1 && deal.players1.length > 2 && ` (${deal.players1.length} players)`}
                  </Text>
                </HStack>
              </Box>
              
              {/* Players List */}
              <VStack align="start" spacing={1} pt={2} w="full" pl={4} >
                {/* Always show first 2 players */}
                {deal.players1 && deal.players1.length > 0 && (
                  <Text fontSize="xs" color={textColor}>
                    {deal.players1Details?.[0] ? 
                      `${deal.players1Details[0].first_name} ${deal.players1Details[0].last_name}${deal.players1Details[0].positions.filter(pos => !pos.includes('-')).length > 0 ? ` (${deal.players1Details[0].positions.filter(pos => !pos.includes('-')).join(', ')})` : ''}` : 
                      `Player ${deal.players1[0]}`
                    }
                  </Text>
                )}
                {deal.players1 && deal.players1.length > 1 && (
                  <Text fontSize="xs" color={textColor}>
                    {deal.players1Details?.[1] ? 
                      `${deal.players1Details[1].first_name} ${deal.players1Details[1].last_name}${deal.players1Details[1].positions.filter(pos => !pos.includes('-')).length > 0 ? ` (${deal.players1Details[1].positions.filter(pos => !pos.includes('-')).join(', ')})` : ''}` : 
                      `Player ${deal.players1[1]}`
                    }
                  </Text>
                )}
              </VStack>
            </VStack>
            
            {/* Team 2 */}
            <VStack align="start" spacing={1} flex={1}>
              <Box w="full">
                <HStack spacing={0} align="center" justify="start">
                  <Box flexShrink={0}>
                    <TeamLogo 
                      team={{
                        name: getTeamInfoById(deal.team2, myLeague)?.name || `Team ${deal.team2}`,
                        logo: getTeamInfoById(deal.team2, myLeague)?.logo,
                        color_primary: getTeamInfoById(deal.team2, myLeague)?.color_primary,
                        color_secondary: getTeamInfoById(deal.team2, myLeague)?.color_secondary,
                        color_text: getTeamInfoById(deal.team2, myLeague)?.color_text
                      }} 
                      size="sm" 
                    />
                  </Box>
                  <Text ml={4} fontSize="s" fontWeight="semibold" color={secondaryText} flex={1}>
                    {getTeamInfoById(deal.team2, myLeague)?.name || `Team ${deal.team2}`}
                    {deal.players2 && deal.players2.length > 2 && ` (${deal.players2.length} players)`}
                  </Text>
                </HStack>
              </Box>
              
              {/* Players List */}
              <VStack align="start" spacing={1} w="full" pt={1} pl={4} >
                {/* Always show first 2 players */}
                {deal.players2 && deal.players2.length > 0 && (
                  <Text fontSize="xs" color={textColor}>
                    {deal.players2Details?.[0] ? 
                      `${deal.players2Details[0].first_name} ${deal.players2Details[0].last_name}${deal.players2Details[0].positions.filter(pos => !pos.includes('-')).length > 0 ? ` (${deal.players2Details[0].positions.filter(pos => !pos.includes('-')).join(', ')})` : ''}` : 
                      `Player ${deal.players2[0]}`
                    }
                  </Text>
                )}
                {deal.players2 && deal.players2.length > 1 && (
                  <Text fontSize="xs" color={textColor}>
                    {deal.players2Details?.[1] ? 
                      `${deal.players2Details[1].first_name} ${deal.players2Details[1].last_name}${deal.players2Details[1].positions.filter(pos => !pos.includes('-')).length > 0 ? ` (${deal.players2Details[1].positions.filter(pos => !pos.includes('-')).join(', ')})` : ''}` : 
                      `Player ${deal.players2[1]}`
                    }
                  </Text>
                )}
              </VStack>
            </VStack>
          </HStack>
          
          {/* Bottom Section - Always at bottom */}
          <Box w="full" mt="auto" pt={4}>
            {/* CTA Button */}
            <Box w="full" display="flex" justifyContent="space-between" alignItems="center">
              {/* Countdown Timer or Status Info */}
              {(() => {
                const statusLower = (deal.status || '').toLowerCase();
                // If completed, show settled at updated
                if (statusLower === 'completed' || statusLower === 'complete') {
                  return (
                    <Text fontSize="xs" color={secondaryText}>
                      Settled {new Date(deal.updated).toLocaleDateString()}
                    </Text>
                  );
                }
                // Check if trade should be marked as expired (but not rejected, canceled, approved, or completed)
                const isExpired = statusLower !== 'under_review' && 
                                 statusLower !== 'league_review' && 
                                 statusLower !== 'rejected' &&
                                 statusLower !== 'canceled' &&
                                 statusLower !== 'approved' &&
                                 statusLower !== 'completed' &&
                                 deal.expires && 
                                 new Date(deal.expires) < new Date();
                
                if (isExpired) {
                  return (
                    <Text fontSize="xs" color={secondaryText}>
                      Expired {new Date(deal.expires).toLocaleDateString()}
                    </Text>
                  );
                }
                
                // Otherwise, use existing logic
                return !['declined', 'expired', 'approved', 'withdrawn', 'rejected', 'canceled'].includes(deal.status) ? (
                  <Text 
                    fontSize="xs" 
                    color={secondaryText}
                    dangerouslySetInnerHTML={{ 
                      __html: deal.status === 'settlement_trade'
                        ? '<span style="color: #48BB78;">Settles at midnight</span>'
                        : deal.status.includes('settlement') 
                        ? getExpiryCountdown(deal.expires).replace(/Expires/g, 'Settles')
                        : getExpiryCountdown(deal.expires)
                    }}
                  />
                ) : (
                  // Show declined/withdrawn/rejected info for terminated trades
                  <Text fontSize="xs" color={secondaryText} textAlign={deal.status === 'rejected' ? 'left' : undefined}>
                    {deal.status === 'declined' || deal.status === 'withdrawn' ? (
                      <>
                        {deal.status === 'declined' ? 'Declined' : 'Withdrawn'} {new Date(deal.updated).toLocaleDateString()} by {
                          !deal.team1_accepted ? 
                            getTeamInfoById(deal.team1, myLeague)?.name || `Team ${deal.team1}` :
                            getTeamInfoById(deal.team2, myLeague)?.name || `Team ${deal.team2}`
                        }
                      </>
                    ) : deal.status === 'rejected' ? (
                      <>
                        Canceled {new Date(deal.updated).toLocaleDateString()} by the commissioner
                      </>
                    ) : deal.status === 'canceled' ? (
                      <>
                        Canceled {new Date(deal.created).toLocaleDateString()}
                      </>
                    ) : (
                      // For other terminated statuses (expired, rejected, approved), show just the status and date
                      `${getStatusText(deal.status)} ${new Date(deal.updated).toLocaleDateString()}`
                    )}
                  </Text>
                );
              })()}
              
              {/* View Details Button - always on the right */}
              <Box mr={-2}>
                <Button 
                  size="sm" 
                  bg={(() => {
                    // Check if trade should be marked as expired
                    const isExpired = deal.status !== 'under_review' && 
                                     deal.status !== 'league_review' && 
                                     deal.expires && 
                                     new Date(deal.expires) < new Date();
                    
                    // If expired, use transparent styling
                    if (isExpired) {
                      return "transparent";
                    }
                    
                    // Otherwise, use existing logic
                    return ((deal.status === 'proposed' || deal.status === 'countered') && 
                           !((deal.team1_accepted && myTeam === deal.team1) || 
                             (deal.team2_accepted && myTeam === deal.team2))) ? "blue.500" : "transparent";
                  })()}
                  color="white"
                  _hover={(() => {
                    // Check if trade should be marked as expired
                    const isExpired = deal.status !== 'under_review' && 
                                     deal.status !== 'league_review' && 
                                     deal.expires && 
                                     new Date(deal.expires) < new Date();
                    
                    // If expired, use transparent hover
                    if (isExpired) {
                      return { bg: "whiteAlpha.200" };
                    }
                    
                    // Otherwise, use existing logic
                    return ((deal.status === 'proposed' || deal.status === 'countered') && 
                           !((deal.team1_accepted && myTeam === deal.team1) || 
                             (deal.team2_accepted && myTeam === deal.team2))) ? { bg: "blue.700" } : { bg: "whiteAlpha.200" };
                  })()}
                  variant="ghost"
                  onClick={() => handleViewDeal(deal)}
                  px={2}
                  h="24px"
                  leftIcon={(() => {
                    // Check if trade should be marked as expired
                    const isExpired = deal.status !== 'under_review' && 
                                     deal.status !== 'league_review' && 
                                     deal.expires && 
                                     new Date(deal.expires) < new Date();
                    
                    // If expired, no left icon
                    if (isExpired) {
                      return undefined;
                    }
                    
                    // Otherwise, use existing logic
                    return ((deal.status === 'proposed' || deal.status === 'countered') && 
                           !((deal.team1_accepted && myTeam === deal.team1) || 
                             (deal.team2_accepted && myTeam === deal.team2))) ? <FaReply /> : undefined;
                  })()}
                >
                  {(() => {
                    // Check if trade should be marked as expired
                    const isExpired = deal.status !== 'under_review' && 
                                     deal.status !== 'league_review' && 
                                     deal.expires && 
                                     new Date(deal.expires) < new Date();
                    
                    // If expired, always show "View Details"
                    if (isExpired) {
                      return 'View Details';
                    }
                    
                    // Otherwise, use existing logic
                    return (deal.status === 'proposed' || deal.status === 'countered') && 
                           !((deal.team1_accepted && myTeam === deal.team1) || 
                             (deal.team2_accepted && myTeam === deal.team2)) ? 
                      'Respond' : 'View Details';
                  })()}
                </Button>
              </Box>
            </Box>
          </Box>
        </VStack>
      </Box>
    </GlassCard>
  );



  // Visibility for /trades page: hide certain statuses if user's team is not involved
  const filterDealsByVisibility = (dealsToFilter: Deal[]) => {
    // If it's my-trades page, show all deals
    if (isMyTrades) {
      return dealsToFilter;
    }
    
    // For /trades page, filter out pending/proposed/countered/withdrawn/declined
    // if the user's team is not team1 or team2
    const userTeamId = myTeam || currentUserTeamId;
    if (!userTeamId) {
      return dealsToFilter; // If no user team, show all
    }
    
    const hiddenStatuses: Deal['status'][] = ['proposed', 'countered', 'withdrawn', 'declined', 'expired'];
    
    return dealsToFilter.filter(deal => {
      // If status is in the hidden list, check if user's team is involved
      if (hiddenStatuses.includes(deal.status)) {
        const isUserTeamInvolved = deal.team1 === userTeamId || deal.team2 === userTeamId;
        // Only show if user's team is involved
        return isUserTeamInvolved;
      }
      // For all other statuses, show the deal
      return true;
    });
  };

  // Get filtered deals based on current tab and visibility rules
  const getFilteredDealsForTab = (tabName: string) => {
    if (!deals) return [];
    
    let filteredDeals: Deal[] = [];
    
    switch (tabName) {
      case 'all':
        filteredDeals = deals;
        break;
      case 'pending':
        filteredDeals = deals.filter(deal => 
          ['proposed', 'countered', 'under_review', 'league_review', 'accepted'].includes(deal.status) ||
          deal.status.includes('settlement')
        );
        break;
      case 'completed':
        filteredDeals = deals.filter(deal => {
          const s = (deal.status || '').toLowerCase();
          return s === 'completed' || s === 'complete';
        });
        break;
      case 'terminated':
        filteredDeals = deals.filter(deal => 
          ['declined', 'rejected', 'expired', 'withdrawn', 'canceled'].includes(deal.status)
        );
        break;
      default:
        filteredDeals = deals;
    }
    
    // Apply visibility filtering for /trades page
    return filterDealsByVisibility(filteredDeals);
  };

  const activeTabIndex = tabOptions.findIndex(tab => tab.value === activeTab);

  if (loading) {
    return (
      <Container maxW="container.xl" className="minimal-panel-padding">
        <VStack spacing={8} align="stretch">
          <Flex align="center" justify="space-between" mb={4} flexWrap="wrap" gap={3}>
            <PageTitle>
              {isMyTrades ? 'My Trades' : 'Trades'}
            </PageTitle>
            <Button colorScheme="blue" size="sm" onClick={() => setIsNewTradeModalOpen(true)}>
              NEW TRADE
            </Button>
          </Flex>

          <Box mb={4}>
            <Select
              value={activeTab}
              onChange={(e) => setActiveTab(e.target.value)}
              display={{ base: 'block', md: 'none' }}
              size="sm"
              mb={2}
            >
              {tabOptions.map(tab => (
                <option key={tab.value} value={tab.value}>
                  {tab.label}
                </option>
              ))}
            </Select>
            <Tabs
              colorScheme="blue"
              variant="enclosed"
              index={activeTabIndex >= 0 ? activeTabIndex : 0}
              onChange={(index) => {
                const option = tabOptions[index];
                if (option) setActiveTab(option.value);
              }}
            >
              <TabList display={{ base: 'none', md: 'flex' }}>
                {tabOptions.map(tab => (
                  <Tab key={tab.value}>{tab.label}</Tab>
                ))}
              </TabList>

              <TabPanels>
                {tabOptions.map(tab => (
                  <TabPanel key={tab.value}>
                    <VStack spacing={4} align="stretch">
                      <Box display="flex" justifyContent="center" py={8}>
                        <VStack spacing={4}>
                          <Spinner size="lg" />
                          <Text>Loading trades...</Text>
                        </VStack>
                      </Box>
                    </VStack>
                  </TabPanel>
                ))}
              </TabPanels>
            </Tabs>
          </Box>
        </VStack>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxW="container.xl" py={8}>
        <Alert status="error">
          <AlertIcon />
          Error loading trades: {error}
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxW="container.xl" className="minimal-panel-padding">
      <VStack spacing={8} align="stretch">
        {/* Header */}
        <Flex align="center" justify="space-between" mb={4} flexWrap="wrap" gap={3}>
          <PageTitle>
            {isMyTrades ? 'My Trades' : 'Trades'}
          </PageTitle>
          <Button colorScheme="blue" size="sm" display={{ base: 'inline-flex', md: 'none' }} onClick={() => setIsNewTradeModalOpen(true)}>
            NEW TRADE
          </Button>
        </Flex>

        {/* Tabs */}
        <Box mb={4}>
          <Select
            value={activeTab}
            onChange={(e) => setActiveTab(e.target.value)}
            display={{ base: 'block', md: 'none' }}
            size="md"
            width="200px"
            mb={2}
          >
            {tabOptions.map(tab => (
              <option key={tab.value} value={tab.value}>
                {tab.label}
              </option>
            ))}
          </Select>
          <Tabs
            colorScheme="blue"
            variant="enclosed"
            index={activeTabIndex >= 0 ? activeTabIndex : 0}
            onChange={(index) => {
              const option = tabOptions[index];
              if (option) setActiveTab(option.value);
            }}
          >
            <TabList display={{ base: 'none', md: 'flex' }} alignItems="center">
              {tabOptions.map(tab => (
                <Tab key={tab.value}>{tab.label}</Tab>
              ))}
              <Box ml="auto" display="flex">
                <Button colorScheme="blue" size="sm" onClick={() => setIsNewTradeModalOpen(true)}>
                  NEW TRADE
                </Button>
              </Box>
            </TabList>

            <TabPanels>
          <TabPanel>
            <VStack spacing={4} align="stretch">
              {loading ? (
                <Box display="flex" justifyContent="center" py={8}>
                  <VStack spacing={4}>
                    <Spinner size="lg" />
                    <Text>Loading trades...</Text>
                  </VStack>
                </Box>
              ) : (() => {
                const filteredDeals = getFilteredDealsForTab('pending');
                return filteredDeals.length === 0 ? (
                  <Text color={secondaryText} textAlign="center" py={8}>
                    No pending trades
                  </Text>
                ) : (
                  <Box>
                    <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={4}>
                      {filteredDeals.map(renderDealCard)}
                    </SimpleGrid>
                  </Box>
                );
              })()}
            </VStack>
          </TabPanel>

          <TabPanel>
            <VStack spacing={4} align="stretch">
              {loading ? (
                <Box display="flex" justifyContent="center" py={8}>
                  <VStack spacing={4}>
                    <Spinner size="lg" />
                    <Text>Loading trades...</Text>
                  </VStack>
                </Box>
              ) : (() => {
                const filteredDeals = getFilteredDealsForTab('completed');
                return filteredDeals.length === 0 ? (
                  <Text color={secondaryText} textAlign="center" py={8}>
                    No completed trades
                  </Text>
                ) : (
                  <Box>
                    <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={4}>
                      {filteredDeals.map(renderDealCard)}
                    </SimpleGrid>
                  </Box>
                );
              })()}
            </VStack>
          </TabPanel>

          <TabPanel>
            <VStack spacing={4} align="stretch">
              {loading ? (
                <Box display="flex" justifyContent="center" py={8}>
                  <VStack spacing={4}>
                    <Spinner size="lg" />
                    <Text>Loading trades...</Text>
                  </VStack>
                </Box>
              ) : (() => {
                const filteredDeals = getFilteredDealsForTab('terminated');
                return filteredDeals.length === 0 ? (
                  <Text color={secondaryText} textAlign="center" py={8}>
                    No terminated trades (expired, withdrawn, canceled, or declined)
                  </Text>
                ) : (
                  <Box>
                    <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={4}>
                      {filteredDeals.map(renderDealCard)}
                    </SimpleGrid>
                  </Box>
                );
              })()}
            </VStack>
          </TabPanel>

          <TabPanel>
            <VStack spacing={4} align="stretch">
              {/* Status Filter for All tab */}
              <Box>
                <HStack spacing={4}>
                  {/* Status Multi-Select */}
                  <Popover
                    isOpen={statusPopoverOpen}
                    onOpen={() => {
                      setStatusPopoverOpen(true);
                      setPendingStatuses([...selectedStatuses]); // Initialize pending with current selection
                    }}
                    onClose={() => {
                      setStatusPopoverOpen(false);
                      setPendingStatuses([]); // Clear pending when closing without applying
                    }}
                    placement="bottom-start"
                  >
                    <PopoverTrigger>
                      <Button onClick={() => setStatusPopoverOpen(!statusPopoverOpen)} variant="outline" colorScheme="blue">
                        {selectedStatuses.length > 0 ? `${selectedStatuses.length} Status${selectedStatuses.length > 1 ? 'es' : ''}` : 'Filter by Status'}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent bg="gray.800" color="white" minW="200px">
                      <PopoverArrow />
                      <PopoverCloseButton onClick={() => {
                        setStatusPopoverOpen(false);
                        setPendingStatuses([]); // Clear pending when manually closing
                      }} />
                      <PopoverBody>
                        <VStack align="flex-start" spacing={1} pb={2}>
                          {['Proposed', 'Countered', 'Accepted', 'Declined', 'Expired', 'Under review', 'Approved', 'Rejected', 'Withdrawn', 'League review', ].map((status) => (
                            <Checkbox
                              key={status}
                              isChecked={pendingStatuses.includes(mapStatusToApi(status))}
                              onChange={() => toggleStatus(status)}
                              colorScheme="blue"
                            >
                              {getStatusText(status)}
                            </Checkbox>
                          ))}
                        </VStack>
                        
                        {/* Filter Button */}
                        <Box display="flex" justifyContent="flex-end" pt={2} borderTop="1px solid" borderColor="gray.600">
                          <Button
                            size="sm"
                            colorScheme="blue"
                            onClick={applyFilters}
                          >
                            Apply Filters
                          </Button>
                        </Box>
                      </PopoverBody>
                    </PopoverContent>
                  </Popover>
                </HStack>
                
                {/* Status Filter Chips */}
                {selectedStatuses.length > 0 && (
                  <HStack spacing={2} flexWrap="wrap" mt={2}>
                    {selectedStatuses.map((status) => (
                      <Tag key={status} size="md" colorScheme="blue" borderRadius="full">
                        <TagLabel>{getStatusText(status)}</TagLabel>
                        <TagCloseButton onClick={() => removeStatus(status)} />
                      </Tag>
                    ))}
                  </HStack>
                )}
              </Box>
              
              {loading ? (
                <Box display="flex" justifyContent="center" py={8}>
                  <VStack spacing={4}>
                    <Spinner size="lg" />
                    <Text>Loading trades...</Text>
                  </VStack>
                </Box>
              ) : (() => {
                const filteredDeals = getFilteredDealsForTab('all');
                return filteredDeals.length > 0 ? (
                  <Box>
                    <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={4}>
                      {filteredDeals.map(renderDealCard)}
                    </SimpleGrid>
                  </Box>
                ) : (
                  <Text color={secondaryText} textAlign="center" py={8}>
                    No trades found
                  </Text>
                );
              })()}
            </VStack>
          </TabPanel>
        </TabPanels>
          </Tabs>
        </Box>
      </VStack>

      {/* Trade Modal */}
      {isTradeModalOpen && selectedDeal && selectedDeal.team1 && selectedDeal.team2 && (
        <TradeModal
          isOpen={isTradeModalOpen}
          onClose={handleCloseTradeModal}
          tradeId={selectedDeal.id.toString()}
          original={selectedDeal.original}
          team1={selectedDeal.team1}
          team2={selectedDeal.team2}
          team1Info={getTeamInfoById(selectedDeal.team1, myLeague)}
          team2Info={getTeamInfoById(selectedDeal.team2, myLeague)}
          initialPlayers1={selectedDeal.players1Details || []}
          initialPlayers2={selectedDeal.players2Details || []}
          initialNote={selectedDeal.note}
          lastUpdated={selectedDeal.updated}
          expires={selectedDeal.expires}
          updatedBy={selectedDeal.created_by}
          status={selectedDeal.status}
          team1Accepted={selectedDeal.team1_accepted}
          team2Accepted={selectedDeal.team2_accepted}
          viewedAt={selectedDeal.viewed_at}
          initialProtests={selectedDeal.protests || []}
        />
      )}
          {/* NewTrade Modal */}
        {isNewTradeModalOpen  && (
        <TradeModal
          isOpen={isNewTradeModalOpen}
          onClose={handleCloseNewTradeModal}
          team1={parseInt(myTeam) || undefined}
          initialPlayers1={[]}
          initialPlayers2={[]}
        />
      )}
    </Container>
  );
};

export default Trades; 